import { queryClient } from "@/api/constants"

import { Sentry } from "@/telemetry/sentry"
import { debounced } from "@/utils/debounce"
import { ProjectItem, ProjectService } from "../project/projects-service"
import { fetchOrExtractPreview } from "./previews-processing"
import { PreviewService } from "./previews-service"
import { PreviewSize } from "@/store/previews/previews-types"
import { PhotoRecord } from "@/lib/catalog"
import { photosAtom, exportablePhotosAtom } from "../photo/photos-state"
import { updateCurrentProject } from "../project/projects-state"
import { store } from "../store"

export class PreviewSyncService extends PreviewService {
	//Deprecated in favor of syncProjectCoverIfNeededDebounced, kept in case we need more than 1 project cover
	// /**
	//  *
	//  * Synchronize project thumbnails:
	//  *   - Upload project thumbnail images
	//  *   - Persist image URLs in backend project record
	//  *   - Disassociate cloud URLs & sync status from PreviewRecords no longer used as thumbnails
	//  *
	//  * @param project `ProjectItem` for which to sync thumbnails
	//  *
	//  */
	// @debounced(2000)
	// static syncProjectThumbnailsDebounced(
	// 	project: ProjectItem,
	// 	photos: Record<string, PhotoRecord>,
	// ) {
	// 	PreviewSyncService.syncProjectThumbnails(project, photos).catch((e) => {
	// 		Sentry.captureException(e)
	// 	})
	// }

	// static async syncProjectThumbnails(project: ProjectItem, photos: Record<string, PhotoRecord>) {
	// 	if (!project.previewThumbnailPhotoIds || project.previewThumbnailPhotoIds.length === 0) {
	// 		return
	// 	}

	// 	const previewsMap = await fetchPreviewsByPhotoIds(
	// 		project.id,
	// 		project.previewThumbnailPhotoIds,
	// 		PreviewSize.medium,
	// 	)

	// 	const missingPreviews = project.previewThumbnailPhotoIds.filter(
	// 		(photoId) => previewsMap.get(photoId) === undefined,
	// 	)

	// 	//For photos missing preview, we will kick off extration and they will be synced when extracted
	// 	missingPreviews.forEach((photoId) => {
	// 		const photo: PhotoRecord | undefined = photos[photoId]
	// 		if (!photo) return
	// 		//TODO: should we consider skipping the queue so cover photos are available immediately?
	// 		addExtractPreviewTask(project, photo, PreviewSize.medium)
	// 	})

	// 	const previews = project.previewThumbnailPhotoIds
	// 		.map((photoId) => previewsMap.get(photoId))
	// 		.filter(isDefined)

	// 	const previewsNeedingUpload: PreviewRecord[] = previews.filter(
	// 		(preview) => !preview.cloudUrl,
	// 	)
	// 	if (previewsNeedingUpload.length > 0) {
	// 		await this.uploadPreviews(previewsNeedingUpload)
	// 	}

	// 	const previewThumbnailUrls: string[] = previews
	// 		.filter((preview) => !!preview.cloudUrl)
	// 		.map((preview) => preview.cloudUrl as string)

	// 	if (!projectIsDeleted(project.id)) {
	// 		await ProjectService.updateProject(project, { previewThumbnailUrls }).then(() =>
	// 			queryClient.invalidateQueries({ queryKey: ["projects"] }),
	// 		)
	// 	}

	// 	const previewIdToBeMarkedForUrlRemovalMap: {
	// 		[previewId: string]: {
	// 			shouldRemove: boolean
	// 			preview: PreviewRecord
	// 		}
	// 	} = {}

	// 	const syncedPreviews = await fetchSyncedPreviews(project.id)
	// 	syncedPreviews.forEach((preview) => {
	// 		previewIdToBeMarkedForUrlRemovalMap[preview.id] = {
	// 			shouldRemove: true,
	// 			preview,
	// 		}
	// 	})
	// 	previews.forEach((preview) => {
	// 		const item = previewIdToBeMarkedForUrlRemovalMap[preview.id]
	// 		if (item) {
	// 			item.shouldRemove = false
	// 		}
	// 	})

	// 	for (const previewId in previewIdToBeMarkedForUrlRemovalMap) {
	// 		const item = previewIdToBeMarkedForUrlRemovalMap[previewId]
	// 		if (item?.shouldRemove) {
	// 			removePreviewUrl(item.preview)
	// 		}
	// 	}

	// 	console.log("[PreviewService] Thumbnails synced")
	// }

	private static getProjectCoverPhoto(
		project: ProjectItem,
		forceRefresh: boolean = false,
	): PhotoRecord | undefined {
		let coverPhotoId = project.previewThumbnailPhotoIds?.[0]

		//No cover photo selected yet or cover photo no longer exists, select one
		if (forceRefresh || !coverPhotoId || !store.get(photosAtom)[coverPhotoId]) {
			const sortedAvailablePhotos = store.get(exportablePhotosAtom)
			const photo = sortedAvailablePhotos.at(0)
			if (!photo) {
				if (project.previewThumbnailPhotoIds?.[0]) {
					project.previewThumbnailPhotoIds = []
					void updateCurrentProject({ previewThumbnailPhotoIds: [] })
				}
				return undefined
			}
			coverPhotoId = photo.id
			project.previewThumbnailPhotoIds = [photo.id]
			void updateCurrentProject({ previewThumbnailPhotoIds: [photo.id] })
		}

		if (!coverPhotoId) {
			throw new Error("No cover photo selected")
		}

		return store.get(photosAtom)[coverPhotoId]
	}

	@debounced(2000)
	static syncProjectCoverIfNeededDebounced(project: ProjectItem) {
		PreviewSyncService.syncProjectCoverIfNeeded(project).catch((e) => {
			Sentry.captureException(e)
		})
	}

	static async syncProjectCoverIfNeeded(project: ProjectItem, forceRefresh: boolean = false) {
		const photo = this.getProjectCoverPhoto(project, forceRefresh)
		if (project.isSampleProject) {
			// Sample projects are read-only on the backend. Keep local cover-photo
			// selection up to date, but skip remote preview upload / project patching.
			return
		}

		if (!photo) {
			if (project.previewThumbnailUrls?.[0]) {
				//Project has a cover URL but no cover photo, remove the URL
				await ProjectService.updateProject(project, { previewThumbnailUrls: [] })
				await queryClient.invalidateQueries({ queryKey: ["projects"] })
			}
			return
		}

		const preview = await fetchOrExtractPreview(project.id, photo, PreviewSize.medium)
		if (!preview) {
			//No preview could be extracted
			throw new Error("No preview could be extracted for cover photo")
		}

		if (preview.cloudSyncStatus === "synced" && preview.cloudUrl) {
			if (project.previewThumbnailUrls?.[0] === preview.cloudUrl) {
				//Already synced and up to date
				return
			} else {
				//Update project with preview URL
				await ProjectService.updateProject(project, {
					previewThumbnailUrls: [preview.cloudUrl],
				}).then(() => queryClient.invalidateQueries({ queryKey: ["projects"] }))
			}
		} else {
			//Upload preview and update project
			const uploadedPreview = await PreviewService.uploadPreview(preview)
			if (!uploadedPreview.cloudUrl) {
				throw new Error("Uploaded preview has no cloud URL")
			}
			await ProjectService.updateProject(project, {
				previewThumbnailUrls: [uploadedPreview.cloudUrl],
			}).then(() => queryClient.invalidateQueries({ queryKey: ["projects"] }))
		}
	}
}
